/**
 * ScoringTrendStrip - PPG/PAPG/Diff with inline sparklines (Tufte-style).
 *
 * Shows scoring metrics side-by-side with embedded trend sparklines.
 * Sport-agnostic: accepts rows of {label, homeValue, awayValue, homeSparkData, awaySparkData}.
 *
 * @param {Array} rows - [{label, homeValue, awayValue, homeSparkData, awaySparkData, homeColor, awayColor}]
 * @param {Object} homeTeam - {abbreviation}
 * @param {Object} awayTeam - {abbreviation}
 */
import Card from '../../common/Card';
import InlineSparkline from './InlineSparkline';

const ScoringTrendStrip = ({
  rows = [],
  homeTeam = {},
  awayTeam = {},
}) => {
  if (rows.length === 0) return null;

  return (
    <Card className="p-3">
      <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2">
        Scoring Trends
      </h3>

      {/* Column headers */}
      <div className="grid grid-cols-[60px_1fr_1fr] gap-2 mb-1 px-1">
        <div></div>
        <div className="text-[10px] text-purple-400 font-semibold text-center">
          {awayTeam.abbreviation || 'AWAY'}
        </div>
        <div className="text-[10px] text-accent-blue font-semibold text-center">
          {homeTeam.abbreviation || 'HOME'}
        </div>
      </div>

      {/* Metric rows */}
      <div className="space-y-1">
        {rows.map((row, i) => (
          <div
            key={i}
            className="grid grid-cols-[60px_1fr_1fr] gap-2 items-center py-0.5"
          >
            {/* Label */}
            <span className="text-[10px] text-text-secondary font-medium">
              {row.label}
            </span>

            {/* Away: value + sparkline */}
            <div className="flex items-center justify-center gap-1">
              <span className={`font-mono text-xs font-semibold ${
                row.awayColor || 'text-text-primary'
              }`}>
                {row.awayValue != null ? (
                  typeof row.awayValue === 'number' && row.awayValue > 0 && row.label === 'Diff'
                    ? `+${row.awayValue}`
                    : row.awayValue
                ) : '-'}
              </span>
              {row.awaySparkData && row.awaySparkData.length >= 2 && (
                <InlineSparkline
                  data={row.awaySparkData}
                  width={48}
                  height={14}
                  color="#a855f7"
                  referenceLine={typeof row.awayValue === 'number' ? row.awayValue : null}
                />
              )}
            </div>

            {/* Home: value + sparkline */}
            <div className="flex items-center justify-center gap-1">
              <span className={`font-mono text-xs font-semibold ${
                row.homeColor || 'text-text-primary'
              }`}>
                {row.homeValue != null ? (
                  typeof row.homeValue === 'number' && row.homeValue > 0 && row.label === 'Diff'
                    ? `+${row.homeValue}`
                    : row.homeValue
                ) : '-'}
              </span>
              {row.homeSparkData && row.homeSparkData.length >= 2 && (
                <InlineSparkline
                  data={row.homeSparkData}
                  width={48}
                  height={14}
                  color="#00D9FF"
                  referenceLine={typeof row.homeValue === 'number' ? row.homeValue : null}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default ScoringTrendStrip;
